import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import Select from 'react-select';
import { Button, ButtonGroup, Col, ControlLabel, FormCheck, FormControl, FormGroup, Modal, Row } from '@cancercentrum/rcc-bootstrap';
import { incaAccessor } from '@cancercentrum/inca';
import without from 'lodash/without';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImport, faSquareCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faSquare } from '@fortawesome/free-regular-svg-icons';
import classnames from 'classnames';
import { useErrandPanelContext } from '../../ErrandPanel';
import { useIsReviewer } from '../../../../hooks/useIsReviewer';
import { errandButtonUtils } from '../../errandButtonUtils';
import { SimplifiedErrandButtonTags } from '../../constants';
import { ReporterSection } from '../../../ReporterSection';
import { ReviewerSection } from '../../../ReviewerSection';
import { Explanation } from '../../../Explanation';
const REVIEWER_TAGS = [
    SimplifiedErrandButtonTags.Paminnelse,
    SimplifiedErrandButtonTags.Komplettera,
    SimplifiedErrandButtonTags.Remiss
];
const REPORTER_TAGS = [];
const TAGS_TO_REMOVE_REPORTER = [
    SimplifiedErrandButtonTags.ForGodkannande
];
const TAGS_TO_REMOVE_REVIEWER = [
    SimplifiedErrandButtonTags.Inrapporterat,
    SimplifiedErrandButtonTags.AvvaktaCanceranmalan
];
const ACTION_MAP = {
    Skicka_till_inrapportör: {
        normal: 'Skicka_till_inrapportör',
        delspara: 'Skicka_till_inrapportör_och_delspara'
    },
    Återremittera: {
        normal: 'Återremittera',
        delspara: 'Återremittera_och_delspara'
    },
    Skicka_till_RCC: {
        normal: 'Skicka_till_RCC',
        delspara: 'Skicka_till_RCC_och_delspara'
    },
    Skicka_till_annat_rcc: {
        normal: 'Skicka_till_annat_RCC',
        delspara: 'Skicka_till_annat_RCC'
    }
};
export const SendToModal = (props)=>{
    const { onCancel, onSuccess } = props;
    const ctx = useErrandPanelContext();
    const inca = incaAccessor();
    const originalReportingPosition = ctx.reportingPositions.find((p)=>p.id === ctx.errand.document.reportingPositionId);
    const isReviewer = useIsReviewer();
    const tagsToRemove = isReviewer ? TAGS_TO_REMOVE_REVIEWER : TAGS_TO_REMOVE_REPORTER;
    const selectablePositions = useMemo(()=>{
        if (!isReviewer) {
            return ctx.reportingPositions;
        }
        return [
            ...ctx.reportingPositions,
            ...ctx.topPositions
        ];
    }, [
        ctx.reportingPositions,
        ctx.topPositions,
        isReviewer
    ]);
    const selectableTags = useMemo(()=>{
        return errandButtonUtils.getSelectableTags(isReviewer ? REVIEWER_TAGS : REPORTER_TAGS, tagsToRemove, ctx);
    }, [
        isReviewer,
        tagsToRemove,
        ctx
    ]);
    const [tags, setTags] = useState(errandButtonUtils.getInitiallySelectedTags(selectableTags, ctx));
    const [positionId, setPositionId] = useState(null);
    const [comment, setComment] = useState(ctx.errandComment);
    const commentRequired = ctx.formIncomplete && !isReviewer;
    const isValid = (!isReviewer || !!positionId) && (commentRequired && comment || !commentRequired);
    const isDelsparad = !!ctx.errand.document.lastContributedToText;
    const [delspara, setDelspara] = useState(isDelsparad);
    const submitReviewer = ()=>{
        const selectedPosition = selectablePositions.find((x)=>x.id === positionId);
        const extraTags = delspara ? [
            SimplifiedErrandButtonTags.Delspara
        ] : [];
        let action;
        let tagString = errandButtonUtils.getTagsString([
            ...tags,
            ...extraTags
        ], selectableTags, tagsToRemove, ctx);
        if (originalReportingPosition?.id === positionId) {
            action = ACTION_MAP['Återremittera'];
        } else if (!selectedPosition?.parentId) {
            action = ACTION_MAP['Skicka_till_annat_rcc'];
            tagString = errandButtonUtils.getTagsString([
                ...tags,
                ...extraTags
            ], selectableTags, [], ctx);
        } else {
            action = ACTION_MAP['Skicka_till_inrapportör'];
        }
        const transitionName = delspara ? action.delspara : action.normal;
        onSuccess(transitionName, {
            errandTags: tagString,
            comment,
            selectedPositionId: positionId
        });
    };
    const submitReporter = ()=>{
        const action = ACTION_MAP['Skicka_till_RCC'];
        const transitionName = delspara ? action.delspara : action.normal;
        onSuccess(transitionName, {
            errandTags: errandButtonUtils.getTagsString([
                ...tags,
                SimplifiedErrandButtonTags.Inrapporterat
            ], selectableTags, tagsToRemove, ctx),
            comment,
            selectedPositionId: inca.user.position.id
        });
    };
    const submit = isReviewer ? submitReviewer : submitReporter;
    const handlePositionChange = (pos)=>{
        setPositionId(pos?.id ?? null);
    };
    return /*#__PURE__*/ _jsxs(Modal, {
        show: true,
        onHide: onCancel,
        backdrop: "static",
        bsSize: "xl",
        animation: false,
        children: [
            /*#__PURE__*/ _jsx(Modal.Header, {
                closeButton: true,
                children: /*#__PURE__*/ _jsx(Modal.Title, {
                    children: /*#__PURE__*/ _jsx("strong", {
                        children: "Skicka till"
                    })
                })
            }),
            /*#__PURE__*/ _jsxs(Modal.Body, {
                children: [
                    /*#__PURE__*/ _jsx(ReporterSection, {
                        children: ctx.formIncomplete && /*#__PURE__*/ _jsx("p", {
                            children: "Eftersom formuläret inte fyllts i komplett skickas det till RCC i din region för handläggning. Ange orsak till varför formuläret är inkomplett."
                        })
                    }),
                    /*#__PURE__*/ _jsxs(ReviewerSection, {
                        children: [
                            /*#__PURE__*/ _jsxs(FormGroup, {
                                componentClass: Row,
                                children: [
                                    /*#__PURE__*/ _jsx(ControlLabel, {
                                        column: true,
                                        sm: 2,
                                        children: "Skicka till"
                                    }),
                                    /*#__PURE__*/ _jsx(Col, {
                                        sm: 10,
                                        children: /*#__PURE__*/ _jsx(Select, {
                                            options: selectablePositions,
                                            value: selectablePositions.find((x)=>x.id === positionId),
                                            getOptionValue: (x)=>x.id.toString(),
                                            getOptionLabel: (x)=>x.fullNameWithCode,
                                            placeholder: "--Välj--",
                                            onChange: (e)=>handlePositionChange(e)
                                        })
                                    })
                                ]
                            }),
                            !!originalReportingPosition && originalReportingPosition.id !== inca.user.position.id && /*#__PURE__*/ _jsx(FormGroup, {
                                children: /*#__PURE__*/ _jsx(Col, {
                                    sm: {
                                        span: 10,
                                        offset: 2
                                    },
                                    children: /*#__PURE__*/ _jsx(Button, {
                                        bsStyle: "default-1",
                                        title: originalReportingPosition.fullNameWithCode,
                                        onClick: ()=>handlePositionChange(originalReportingPosition),
                                        children: "Återremittera till inrapporterande enhet"
                                    })
                                })
                            }),
                            !!selectableTags.length && /*#__PURE__*/ _jsxs(FormGroup, {
                                componentClass: Row,
                                className: "multi-select-list",
                                children: [
                                    /*#__PURE__*/ _jsx(ControlLabel, {
                                        column: true,
                                        sm: 2,
                                        children: "Lägg till tagg"
                                    }),
                                    /*#__PURE__*/ _jsx(Col, {
                                        sm: 10,
                                        children: /*#__PURE__*/ _jsx(ButtonGroup, {
                                            vertical: false,
                                            children: selectableTags.map((tag)=>/*#__PURE__*/ _jsxs(Button, {
                                                    onClick: ()=>setTags((t)=>{
                                                            if (t.includes(tag)) {
                                                                return without(t, tag);
                                                            }
                                                            return [
                                                                ...t,
                                                                tag
                                                            ];
                                                        }),
                                                    children: [
                                                        /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                                            icon: tags.includes(tag) ? faSquareCheck : faSquare,
                                                            className: classnames('me-2', {
                                                                'text-info': tags.includes(tag)
                                                            })
                                                        }),
                                                        tag
                                                    ]
                                                }, tag))
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(FormGroup, {
                                componentClass: Row,
                                children: [
                                    /*#__PURE__*/ _jsxs(ControlLabel, {
                                        column: true,
                                        sm: 2,
                                        children: [
                                            "Delspara ",
                                            /*#__PURE__*/ _jsx(Explanation, {
                                                children: "Genom att delspara, sparas uppgifter i registret vid varje tillfälle något ändras i ärendet."
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Col, {
                                        sm: 10,
                                        children: /*#__PURE__*/ _jsx(FormCheck, {
                                            checked: delspara,
                                            disabled: isDelsparad,
                                            onChange: (e)=>{
                                                setDelspara(e.target.checked);
                                            }
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(FormGroup, {
                        componentClass: Row,
                        children: [
                            /*#__PURE__*/ _jsx(ControlLabel, {
                                column: true,
                                sm: 2,
                                children: "Kommentar"
                            }),
                            /*#__PURE__*/ _jsx(Col, {
                                sm: 10,
                                children: /*#__PURE__*/ _jsx(FormControl, {
                                    componentClass: "textarea",
                                    value: comment,
                                    onChange: (e)=>setComment(e.target.value)
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Modal.Footer, {
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        icon: faTimes,
                        onClick: onCancel,
                        bsStyle: "default-2",
                        children: "Avbryt"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        icon: faFileImport,
                        bsStyle: "primary",
                        className: "mr-2",
                        onClick: submit,
                        disabled: !isValid,
                        children: "Skicka"
                    })
                ]
            })
        ]
    });
};
